import React, { useEffect, useRef, useState } from 'react';

interface StyleItem {
  label: string;
  level: '매우 높음' | '높음' | '보통' | '낮음' | '매우 낮음';
}

const levelMeta: Record<string, { width: string; color: string; dotColor: string }> = {
  '매우 높음': { width: '92%', color: 'bg-navy', dotColor: 'bg-navy' },
  '높음': { width: '72%', color: 'bg-navy/80', dotColor: 'bg-navy/80' },
  '보통': { width: '50%', color: 'bg-navy/50', dotColor: 'bg-navy/50' },
  '낮음': { width: '30%', color: 'bg-navy/30', dotColor: 'bg-navy/30' },
  '매우 낮음': { width: '12%', color: 'bg-navy/20', dotColor: 'bg-navy/20' },
};

interface StyleBarsProps {
  items: StyleItem[];
}

export default function StyleBars({ items }: StyleBarsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-cream-light px-5 py-10 md:px-8 md:py-14">
      <div
        className={`mx-auto max-w-[480px] transition-all duration-700 md:max-w-[700px] ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="mb-6 flex items-center gap-2">
          <div className="h-px w-6 bg-softgold/40" />
          <span className="text-[11px] font-medium tracking-wider text-softgold">
            선호 스타일
          </span>
        </div>

        <h2 className="mb-8 text-[18px] font-semibold tracking-tight text-navy md:text-[20px]">
          당신이 선호하는 예복 스타일
        </h2>

        <div className="space-y-5">
          {items.map((item, i) => {
            const meta = levelMeta[item.level] || levelMeta['보통'];
            return (
              <div key={i} className="group">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[13px] font-medium text-navy md:text-[14px]">
                    {item.label}
                  </span>
                  <span className="text-[12px] text-navy/50">{item.level}</span>
                </div>
                <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-beige/40">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full ${meta.color} transition-all duration-1000 ease-out ${
                      visible ? '' : 'w-0'
                    }`}
                    style={{
                      width: visible ? meta.width : '0%',
                      transitionDelay: `${i * 120}ms`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}